/**
 * Image generation in the editor (spec 027): the workspace's settings, the
 * open story's look, and one entry per slot with a job. Each running job is
 * polled every second whether or not its panel is open; a 404 poll means the
 * Service no longer knows the job ("Interrupted"). Accepting a draft posts
 * the accept route and then places the media with
 * `storyStore.fillPicture(slotId, mediaId, { uses })`, one story history
 * entry, so one Undo takes the placement back; the media stays in the
 * library (D-10).
 *
 * Nothing here is part of the story document or its history.
 *
 * Spec 030: the owner is a story or a standalone scene (`setOwner`); a
 * scene's look, jobs and briefs go to `/scenes/:id/…` and its placements
 * through `sceneStore.fillPicture` (one scene history entry).
 */
import { create } from 'zustand';
import { apiClient, type ImageJobsRequest, type LookUpdate } from '../api/client';
import type { CharacterFraming, PictureKind, SlotUse } from '../../../Shared/src/@types';
import type {
  FinalStatus, ImageCanvas, ImageDraftView, ImageJobStatus, ImageSettingsView, StoryLookView,
} from '../../../Shared/src/images';
import { DEFAULT_DRAFT_COUNT, type ImageOwnerRef } from '../../../Shared/src/images';
import { refreshPreviewImage } from '../utils/previewMedia';
import { BRIEF_TEXT, libraryPathOf, limitReachedText, placedText } from '../components/imagegen/imageGenText';
import { ownerActions } from '../components/pictures/pictureOwner';

export const POLL_MS = 1000;
const MAX_BACKOFF_MS = 15_000;

export interface SlotGen {
  jobId: string;
  status: ImageJobStatus;
  drafts: ImageDraftView[];
  /** The drafts' canvas from the job (D-28); before the first poll the panel derives it from the slot's framing. */
  canvas?: ImageCanvas;
  /** Object URLs of ready drafts, by index. */
  draftUrls: Record<number, string>;
  pick?: number;
  brief: string;
  accepted?: { mediaId: number; final: FinalStatus };
  /** After "Accept and place": the confirmation line. */
  placed?: string;
  error?: string;
}

/** A Generate waiting on the look gate. */
export interface PendingGenerate {
  slotIds: string[];
  request: Omit<ImageJobsRequest, 'slots'> & { briefs?: Record<string, string>; framings?: Record<string, CharacterFraming> };
}

export type StartResult = 'started' | 'gate' | 'failed';

interface ImageGenState {
  settings?: ImageSettingsView;
  settingsError?: string;
  /** The story owner's id (027); undefined for a scene owner. */
  storyId?: number;
  /** The owner (spec 030): a story or a standalone scene. */
  owner?: ImageOwnerRef;
  look?: StoryLookView;
  entries: Record<string, SlotGen>;
  /** The look gate is open for this Generate. */
  gate: PendingGenerate | null;
  /** A route answered 429: the message every surface shows. */
  limitNotice?: string;

  loadSettings: () => Promise<void>;
  /** The story the editor opened: its look is (re)read. `setOwner({kind: 'story', id})`. */
  setStory: (storyId: number | undefined) => Promise<void>;
  /** The owner the editor opened (spec 030): switching owner clears the entries, as switching story does. */
  setOwner: (owner: ImageOwnerRef | undefined) => Promise<void>;
  loadLook: () => Promise<void>;
  /** Generate for these slots: opens the look gate first when the story's look is not confirmed. */
  generate: (slotIds: string[], request?: PendingGenerate['request']) => Promise<StartResult>;
  /** The gate's "Use this look and generate". */
  confirmLook: (look: LookUpdate) => Promise<StartResult>;
  cancelGate: () => void;
  /** Starts the jobs (the look is confirmed). */
  startJobs: (slotIds: string[], request?: PendingGenerate['request']) => Promise<StartResult>;
  pollOnce: (slotId: string) => Promise<void>;
  cancel: (slotId: string) => Promise<void>;
  retry: (slotId: string, index: number) => Promise<void>;
  more: (slotId: string, brief?: string) => Promise<void>;
  pick: (slotId: string, index: number | undefined) => void;
  accept: (slotId: string, opts: { name: string; uses: SlotUse[]; kind: PictureKind; subject: string }) => Promise<boolean>;
  /** Undo of the placement: one story undo; the media stays (D-10). */
  undoPlacement: (slotId: string) => Promise<void>;
  redraw: (slotId: string) => Promise<void>;
  /**
   * "Write from the story" (D-23): the brief the provider's text model
   * writes for the slot, or the failure's message. Saves a dirty scene
   * first; saves nothing itself (the panel saves through the story store).
   */
  writeBrief: (slotId: string) => Promise<{ ok: true; brief: string } | { ok: false; error: string }>;
  clear: (slotId: string) => void;
  /**
   * After a picture Reset (D-33): stops these slots' jobs (a running one is
   * cancelled on the Service) and drops their entries, so no draft, brief
   * or placement line of the old run comes back.
   */
  forget: (slotIds: string[]) => void;
  reset: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();
const backoff = new Map<string, number>();

const stopPolling = (slotId: string) => {
  const t = timers.get(slotId);
  if (t) clearTimeout(t);
  timers.delete(slotId);
  backoff.delete(slotId);
};

const revokeUrls = (e: SlotGen | undefined) => {
  if (!e) return;
  for (const url of Object.values(e.draftUrls)) {
    try { URL.revokeObjectURL(url); } catch { /* jsdom */ }
  }
};

/** Whether a job still needs polling: drafts in progress, or its accepted picture still drawing. */
const needsPolling = (e: SlotGen) =>
  e.status !== 'cancelled' && e.status !== 'interrupted'
  && (e.status === 'running' || e.accepted?.final === 'drawing');

/** The owner of a state: a scene `owner`, else the story `storyId` names (027's code and tests set only that). */
export const imageOwnerOf = (s: Pick<ImageGenState, 'owner' | 'storyId'>): ImageOwnerRef | undefined =>
  (s.owner?.kind === 'scene' ? s.owner : s.storyId !== undefined ? { kind: 'story', id: s.storyId } : undefined);

/** The owner's routes: a story's (027) or a scene's (030). */
const ownerApi = {
  getLook: (o: ImageOwnerRef) => (o.kind === 'scene' ? apiClient.getSceneLook(o.id) : apiClient.getStoryLook(o.id)),
  saveLook: (o: ImageOwnerRef, body: LookUpdate) => (o.kind === 'scene' ? apiClient.saveSceneLook(o.id, body) : apiClient.saveStoryLook(o.id, body)),
  startJobs: (o: ImageOwnerRef, body: ImageJobsRequest) =>
    (o.kind === 'scene' ? apiClient.startSceneImageJobs(o.id, body) : apiClient.startImageJobs(o.id, body)),
  writeBrief: (o: ImageOwnerRef, slotId: string) =>
    (o.kind === 'scene' ? apiClient.writeScenePictureBrief(o.id, slotId) : apiClient.writePictureBrief(o.id, slotId)),
};

export const useImageGenStore = create<ImageGenState>()((set, get) => {
  const patch = (slotId: string, change: Partial<SlotGen>) =>
    set((s) => (s.entries[slotId] ? { entries: { ...s.entries, [slotId]: { ...s.entries[slotId], ...change } } } : {}));

  const schedule = (slotId: string, ms = POLL_MS) => {
    const prev = timers.get(slotId);
    if (prev) clearTimeout(prev);
    timers.set(slotId, setTimeout(() => {
      timers.delete(slotId);
      void get().pollOnce(slotId);
    }, ms));
  };

  /** A 429 from any route: the shared notice, and the settings re-read for the counts. */
  const noteFailure = (res: { error?: string; monthly?: number }): string => {
    if (res.error === 'monthly_limit_reached') {
      const text = limitReachedText(res.monthly ?? get().settings?.limit.monthly ?? 0);
      set({ limitNotice: text });
      void get().loadSettings();
      return text;
    }
    return res.error || 'Something went wrong. Try again.';
  };

  return {
    entries: {},
    gate: null,

    loadSettings: async () => {
      try {
        const res = await apiClient.getImageSettings();
        if (res.success) set({ settings: res.data, settingsError: undefined });
        else set({ settingsError: res.error });
      } catch (err) {
        set({ settingsError: err instanceof Error ? err.message : 'Could not reach the server' });
      }
    },

    setStory: (storyId) => get().setOwner(storyId === undefined ? undefined : { kind: 'story', id: storyId }),

    setOwner: async (owner) => {
      const current = imageOwnerOf(get());
      if (current?.kind === owner?.kind && current?.id === owner?.id) return;
      for (const id of Object.keys(get().entries)) stopPolling(id);
      for (const e of Object.values(get().entries)) revokeUrls(e);
      set({
        owner, storyId: owner?.kind === 'story' ? owner.id : undefined,
        look: undefined, entries: {}, gate: null, limitNotice: undefined,
      });
      if (owner !== undefined) await get().loadLook();
    },

    loadLook: async () => {
      const owner = imageOwnerOf(get());
      if (owner === undefined) return;
      try {
        const res = await ownerApi.getLook(owner);
        const now = imageOwnerOf(get());
        if (res.success && now?.kind === owner.kind && now.id === owner.id) set({ look: res.data });
      } catch {
        /* the Look row shows nothing */
      }
    },

    generate: async (slotIds, request = {}) => {
      if (slotIds.length === 0) return 'failed';
      if (!get().look) await get().loadLook();
      if (!get().look?.confirmed) {
        set({ gate: { slotIds, request } });
        return 'gate';
      }
      return get().startJobs(slotIds, request);
    },

    confirmLook: async (look) => {
      const owner = imageOwnerOf(get());
      const pending = get().gate;
      if (owner === undefined) return 'failed';
      const res = await ownerApi.saveLook(owner, { ...look, confirm: true });
      if (!res.success) return 'failed';
      set({ gate: null });
      await get().loadLook();
      return pending ? get().startJobs(pending.slotIds, pending.request) : 'started';
    },

    cancelGate: () => set({ gate: null }),

    startJobs: async (slotIds, request = {}) => {
      const owner = imageOwnerOf(get());
      if (owner === undefined) return 'failed';
      // The Service reads the saved owner's map: a dirty scene saves first.
      await ownerActions(owner).saveFirst();
      const { briefs, framings, ...rest } = request;
      const res = await ownerApi.startJobs(owner, {
        ...rest,
        slots: slotIds.map((slotId) => ({
          slotId,
          ...(briefs?.[slotId] ? { brief: briefs[slotId] } : {}),
          ...(framings?.[slotId] ? { framing: framings[slotId] } : {}),
        })),
      });
      if (!res.success) {
        const text = noteFailure(res);
        set((s) => {
          const entries = { ...s.entries };
          for (const id of slotIds) if (entries[id]) entries[id] = { ...entries[id], error: text };
          return { entries };
        });
        return 'failed';
      }
      const count = request.count ?? DEFAULT_DRAFT_COUNT;
      set((s) => {
        const entries = { ...s.entries };
        for (const { jobId, slotId } of res.data.jobs) {
          revokeUrls(entries[slotId]);
          entries[slotId] = {
            jobId,
            status: 'running',
            drafts: Array.from({ length: count }, (_, i) => ({ index: i + 1, status: 'queued' as const })),
            draftUrls: {},
            brief: briefs?.[slotId] ?? '',
          };
        }
        return { entries, limitNotice: undefined };
      });
      for (const { slotId } of res.data.jobs) schedule(slotId);
      void get().loadSettings();
      return 'started';
    },

    pollOnce: async (slotId) => {
      const entry = get().entries[slotId];
      if (!entry) return;
      let res;
      try {
        res = await apiClient.getImageJob(entry.jobId);
      } catch {
        // Network: keep the job and retry with backoff.
        const next = Math.min(MAX_BACKOFF_MS, (backoff.get(slotId) ?? POLL_MS) * 2);
        backoff.set(slotId, next);
        schedule(slotId, next);
        return;
      }
      const current = get().entries[slotId];
      if (!current || current.jobId !== entry.jobId) return;
      if (!res.success) {
        if (res.status === 404) {
          stopPolling(slotId);
          patch(slotId, { status: 'interrupted' });
          return;
        }
        schedule(slotId, Math.min(MAX_BACKOFF_MS, (backoff.get(slotId) ?? POLL_MS) * 2));
        return;
      }
      backoff.delete(slotId);
      const job = res.data;
      const urls = { ...current.draftUrls };
      for (const d of job.drafts) {
        if (d.status !== 'ready' || urls[d.index]) continue;
        try {
          const blob = await apiClient.getDraftBlob(entry.jobId, d.index);
          urls[d.index] = URL.createObjectURL(blob);
        } catch {
          /* the next poll tries again */
        }
      }
      const wasDrawing = current.accepted?.final === 'drawing';
      const next: SlotGen = {
        ...current,
        status: job.status,
        drafts: job.drafts,
        ...(job.canvas ? { canvas: job.canvas } : {}),
        draftUrls: urls,
        brief: current.brief || job.brief,
        ...(current.accepted && job.accepted ? { accepted: job.accepted } : {}),
      };
      set((s) => ({ entries: { ...s.entries, [slotId]: next } }));
      if (wasDrawing && next.accepted?.final === 'ready') {
        // The full image replaces the upscaled draft in open previews.
        void refreshPreviewImage(apiClient, next.accepted.mediaId);
      }
      if (needsPolling(next)) schedule(slotId);
      else stopPolling(slotId);
    },

    cancel: async (slotId) => {
      const e = get().entries[slotId];
      if (!e) return;
      stopPolling(slotId);
      try {
        await apiClient.cancelImageJob(e.jobId);
      } catch {
        /* the job is dropped here either way */
      }
      patch(slotId, { status: 'cancelled' });
    },

    retry: async (slotId, index) => {
      const e = get().entries[slotId];
      if (!e) return;
      const res = await apiClient.retryDraft(e.jobId, index);
      if (!res.success) {
        patch(slotId, { error: noteFailure(res) });
        return;
      }
      patch(slotId, { status: res.data.status, drafts: res.data.drafts, error: undefined });
      schedule(slotId);
      void get().loadSettings();
    },

    more: async (slotId, brief) => {
      const e = get().entries[slotId];
      if (!e) return;
      const res = await apiClient.moreDrafts(e.jobId, brief);
      if (!res.success) {
        patch(slotId, { error: noteFailure(res) });
        return;
      }
      patch(slotId, { status: res.data.status, drafts: res.data.drafts, brief: res.data.brief, error: undefined });
      schedule(slotId);
      void get().loadSettings();
    },

    pick: (slotId, index) => patch(slotId, { pick: index }),

    accept: async (slotId, { name, uses, kind, subject }) => {
      const e = get().entries[slotId];
      if (!e || e.pick === undefined) return false;
      const res = await apiClient.acceptDraft(e.jobId, e.pick, name);
      if (!res.success) {
        patch(slotId, { error: res.error });
        return false;
      }
      const { mediaId } = res.data;
      patch(slotId, { accepted: { mediaId, final: 'drawing' }, error: undefined });
      const placed = await ownerActions(imageOwnerOf(get())).fillPicture(slotId, mediaId, { uses });
      if (placed.ok) {
        patch(slotId, { placed: placedText(libraryPathOf(kind, subject), placed.places, kind) });
        // A new picture may replace one in the previous look (D-26): the Look row's line follows.
        if (get().look?.oldLook) void get().loadLook();
      } else {
        patch(slotId, { error: "The picture is in the library but couldn't be placed. Try Choose from library." });
      }
      schedule(slotId);
      void get().loadSettings();
      return placed.ok;
    },

    undoPlacement: async (slotId) => {
      await ownerActions(imageOwnerOf(get())).undo();
      patch(slotId, { placed: undefined });
      // The picture put back may be in the previous look (D-26).
      void get().loadLook();
    },

    redraw: async (slotId) => {
      const e = get().entries[slotId];
      if (!e?.accepted) return;
      const res = await apiClient.redrawMedia(e.accepted.mediaId);
      if (!res.success) {
        patch(slotId, { error: noteFailure(res) });
        return;
      }
      patch(slotId, { accepted: { mediaId: e.accepted.mediaId, final: 'drawing' }, error: undefined });
      schedule(slotId);
    },

    writeBrief: async (slotId) => {
      const owner = imageOwnerOf(get());
      if (owner === undefined) return { ok: false, error: BRIEF_TEXT.writeFailed };
      await ownerActions(owner).saveFirst();
      try {
        const res = await ownerApi.writeBrief(owner, slotId);
        void get().loadSettings();
        if (res.success) return { ok: true, brief: res.data.brief };
        if (res.error === 'monthly_limit_reached') return { ok: false, error: noteFailure(res) };
        return { ok: false, error: BRIEF_TEXT.writeFailed };
      } catch {
        return { ok: false, error: BRIEF_TEXT.writeFailed };
      }
    },

    clear: (slotId) => {
      stopPolling(slotId);
      set((s) => {
        const entries = { ...s.entries };
        revokeUrls(entries[slotId]);
        delete entries[slotId];
        return { entries };
      });
    },

    forget: (slotIds) => {
      for (const slotId of slotIds) {
        const e = get().entries[slotId];
        if (!e) continue;
        if (e.status === 'running') {
          try {
            void Promise.resolve(apiClient.cancelImageJob(e.jobId)).catch(() => undefined);
          } catch {
            /* the entry is dropped here either way */
          }
        }
        get().clear(slotId);
      }
      // A reset picture may have been in the previous look (D-26): the Look row's line follows.
      if (get().look?.oldLook) void get().loadLook();
    },

    reset: () => {
      for (const id of Object.keys(get().entries)) stopPolling(id);
      for (const e of Object.values(get().entries)) revokeUrls(e);
      set({ settings: undefined, settingsError: undefined, storyId: undefined, owner: undefined, look: undefined, entries: {}, gate: null, limitNotice: undefined });
    },
  };
});
